import {Box, createStyles, Modal, Switch, Text, Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {isNodeTesting} from 'lib/Browser';
import {copyLLMTableToClipboard} from 'llm/markdown/copyLLMTable';
import {parseTableData} from 'llm/markdown/LLMMarkdownUtils';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import useHoverState from 'ui-library/hooks/useHoverState';
import {Table, Column, Cell} from 'ui-library/simple-table/SimpleTable';

export interface LLMMarkdownTableClassNames {
  simpleTableHeader?: string;
  simpleTableHeaderCell?: string;
  simpleTableRow?: string;
  tableCopyButton?: string;
}

interface SplitContentByCitationsResult {
  citationElements: Array<React.ReactNode>;
  nonCitationElements: Array<React.ReactNode>;
}

const splitContentByCitations = (content: Array<React.ReactNode>): SplitContentByCitationsResult => {
  const citationElements: Array<React.ReactNode> = [];
  const nonCitationElements: Array<React.ReactNode> = [];

  content.forEach((item) => {
    const isCitation =
      React.isValidElement(item) &&
      typeof item.type === 'function' &&
      item.props &&
      item.props.node &&
      item.props.node.tagName === 'citationchip';

    if (isCitation) {
      citationElements.push(item);
    } else {
      nonCitationElements.push(item);
    }
  });

  return {citationElements, nonCitationElements};
};

export interface TableWithCitationsProps {
  children: unknown;
  classNames?: LLMMarkdownTableClassNames;
  title?: string;
  subtitle?: string;
}

export function CellWithCitations({
  citationElements,
  nonCitationElements,
  showCitations,
}: {
  citationElements: Array<React.ReactNode>;
  nonCitationElements: Array<React.ReactNode>;
  showCitations: boolean;
}): JSX.Element {
  const [isCellHovered, cellHoverProps] = useHoverState();

  return (
    <Cell hoverProps={cellHoverProps}>
      {nonCitationElements}
      <span
        className={css(!showCitations && !isCellHovered && styles.hiddenCitation)}
        {...getTestProps({component: 'CellWithCitations', element: 'citationElements'})}
      >
        {citationElements}
      </span>
    </Cell>
  );
}

const COPIED_RESET_MS = 2000;
const DEFAULT_EXPANDED_TABLE_TITLE = 'Untitled table';
const DEFAULT_TABLE_HEADER_HEIGHT_PX = 40;
const EXPANDED_TABLE_HEADER_HEIGHT_PX = 52;
const EXPANDED_TABLE_MAX_COLUMN_WIDTH_PX = 1100;

function measureInlineTableWidthPx(container: HTMLElement): number {
  const simpleTable = container.querySelector('.simpleTable');
  if (simpleTable instanceof HTMLElement) {
    return Math.max(container.offsetWidth, simpleTable.scrollWidth);
  }
  return container.offsetWidth;
}

const useTableStyles = createStyles((theme) => ({
  tableHeaderActions: {
    position: 'absolute',
    top: 0,
    right: theme.spacing.md,
    height: DEFAULT_TABLE_HEADER_HEIGHT_PX,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    zIndex: 1,
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 120ms ease-in-out',
  },
  tableActionButton: {
    boxShadow: 'none',
  },
  tableActionControl: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  tableSurface: {
    position: 'relative',
    [`&:hover [data-llm-table-header-actions], &:focus-within [data-llm-table-header-actions]`]: {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
  expandedTablePanelScroll: {
    width: '100%',
    maxHeight: 'calc(100vh - 200px)',
    overflowY: 'auto',
  },
  expandedTableOverlayGrid: {
    display: 'grid',
    width: '100%',
    minWidth: 0,
    [`&:hover [data-llm-table-expanded-copy], &:focus-within [data-llm-table-expanded-copy]`]: {
      opacity: 1,
      pointerEvents: 'auto',
    },
    '& > *': {
      gridArea: '1 / 1',
    },
  },
  expandedTableScrollHost: {
    width: '100%',
    minWidth: 0,
  },
  expandedTableCopyActions: {
    justifySelf: 'end',
    alignSelf: 'start',
    height: EXPANDED_TABLE_HEADER_HEIGHT_PX,
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing.md,
    zIndex: 2,
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 120ms ease-in-out',
  },
  markdownTable: {
    '& .simpleTable-header-cell, & .simpleTable-cell': {
      width: 'auto',
    },
  },
  expandedSimpleTable: {
    width: '100%',
    minWidth: 0,
    '& .simpleTable': {
      width: '100%',
      backgroundColor: theme.white,
      borderRadius: theme.radius.lg,
      marginBottom: 0,
    },
    '& .simpleTable-table': {
      width: 'auto',
      minWidth: '100%',
    },
    '& .simpleTable-header-cell, & .simpleTable-cell': {
      verticalAlign: 'top',
    },
    '& .simpleTable-header-cell:first-child, & .simpleTable-cell:first-child': {
      width: '1%',
      whiteSpace: 'nowrap',
    },
    '& .simpleTable-header': {
      height: 'auto',
      minHeight: EXPANDED_TABLE_HEADER_HEIGHT_PX,
    },
    '& .simpleTable-header-cell': {
      whiteSpace: 'nowrap',
      fontSize: theme.fontSizes.xl,
      lineHeight: String(theme.lineHeight),
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    },
    '& .simpleTable-header-cell:last-child': {
      // Reserve space for the copy control (Nucleus md inset + mini button).
      paddingRight: `calc(${theme.spacing.lg} + 28px + ${theme.spacing.md})`,
    },
    '& .simpleTable-row': {
      height: 'auto',
      minHeight: 'unset',
    },
    '& .simpleTable-cell:not(:first-child)': {
      width: 'auto',
      maxWidth: EXPANDED_TABLE_MAX_COLUMN_WIDTH_PX,
      whiteSpace: 'normal',
      overflowWrap: 'break-word',
    },
    '& .simpleTable-cell': {
      fontSize: theme.fontSizes.xl,
      lineHeight: String(theme.lineHeight),
      padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    },
    '& .simpleTable-footer': {
      height: 'auto',
      minHeight: 52,
    },
    '& .simpleTable-footer-cell': {
      fontSize: theme.fontSizes.xl,
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    },
  },
  expandedModalContent: {
    maxHeight: `calc(100vh - 48px)`,
  },
  expandedModalHeader: {
    // The panel is shrink-to-fit so it hugs narrow tables. A long title would otherwise contribute its
    // full unwrapped width: zeroing the width keeps the header out of that intrinsic measurement, and the
    // percentage min-width stretches it back once the panel width has been resolved from the table.
    width: 0,
    minWidth: '100%',
  },
  expandedModalBody: {
    backgroundColor: theme.colors.gray[3],
  },
  expandedModalSubtitle: {
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.md,
    marginTop: theme.spacing.xs,
  },
}));

function CopyTableButton({
  headers,
  rows,
}: {
  headers: Array<string>;
  rows: Array<Array<Array<React.ReactNode>>>;
}): JSX.Element {
  const {classes} = useTableStyles();
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const handleClick = useCallback(async () => {
    const success = await copyLLMTableToClipboard({headers, rows});
    if (!success) {
      return;
    }
    setCopied(true);
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setCopied(false);
      timeoutRef.current = null;
    }, COPIED_RESET_MS);
  }, [headers, rows]);

  const tooltipLabel = copied ? 'Copied' : 'Copy to clipboard';

  return (
    <Tooltip label={tooltipLabel} withArrow={true} withinPortal={true} zIndex={ZINDEX_FLOATING_UI}>
      <span className={classes.tableActionControl}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MINI}
          className={classes.tableActionButton}
          icon={copied ? 'check' : 'copy'}
          aria-label="Copy table"
          iconAltText="Copy table"
          onClick={handleClick}
          {...getTestProps({component: 'TableWithCitations', element: 'copyButton'})}
        />
      </span>
    </Tooltip>
  );
}

function InlineTableHeaderActions({
  className,
  headers,
  rows,
  onExpandClick,
}: {
  className: string;
  headers: Array<string>;
  rows: Array<Array<Array<React.ReactNode>>>;
  onExpandClick: () => void;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useTableStyles();

  return (
    <Box className={className} data-llm-table-header-actions={true}>
      <CopyTableButton headers={headers} rows={rows} />
      <Tooltip label="Expand table" withArrow={true}>
        <span className={classes.tableActionControl}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.MINI}
            className={classes.tableActionButton}
            icon="expand-right-full"
            aria-label="Expand table"
            iconAltText="Expand table"
            aria-expanded={false}
            onClick={onExpandClick}
            {...getTestProps({component: 'TableWithCitations', element: 'expandButton'})}
          />
        </span>
      </Tooltip>
    </Box>
  );
}

interface LLMMarkdownTableContentProps {
  headers: Array<string>;
  processedRows: Array<
    Array<{
      citationElements: Array<React.ReactNode>;
      nonCitationElements: Array<React.ReactNode>;
    }>
  >;
  showCitations: boolean;
  footer: React.ReactNode | undefined;
  tableClassName?: string;
  fitTableToContent?: boolean;
  rowsForCopy: Array<Array<Array<React.ReactNode>>>;
  onExpandClick: () => void;
  showInlineHeaderActions?: boolean;
  showExpandedCopyButton?: boolean;
  markdownTableClassNames?: LLMMarkdownTableClassNames;
}

function LLMMarkdownTableContent({
  headers,
  processedRows,
  showCitations,
  footer,
  tableClassName,
  fitTableToContent = false,
  rowsForCopy,
  onExpandClick,
  showInlineHeaderActions = false,
  showExpandedCopyButton = false,
  markdownTableClassNames,
}: LLMMarkdownTableContentProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useTableStyles();
  const rowCount = processedRows.length;
  const hasTableSurface = showInlineHeaderActions || showExpandedCopyButton;

  const simpleTableClassNames = markdownTableClassNames
    ? {
        header: markdownTableClassNames.simpleTableHeader,
        headerCell: markdownTableClassNames.simpleTableHeaderCell,
        row: markdownTableClassNames.simpleTableRow,
      }
    : undefined;

  return (
    <div className={cx(classes.markdownTable, tableClassName, hasTableSurface && classes.tableSurface)}>
      {showInlineHeaderActions && (
        <InlineTableHeaderActions
          className={classNames(classes.tableHeaderActions, markdownTableClassNames?.tableCopyButton)}
          headers={headers}
          rows={rowsForCopy}
          onExpandClick={onExpandClick}
        />
      )}
      {showExpandedCopyButton && (
        <Box className={classes.expandedTableCopyActions} data-llm-table-expanded-copy={true}>
          <CopyTableButton headers={headers} rows={rowsForCopy} />
        </Box>
      )}
      <Table
        numRows={rowCount}
        footer={footer}
        overflowVisible={fitTableToContent}
        fitContent={fitTableToContent}
        tableLayout="auto"
        classNames={simpleTableClassNames}
      >
        {headers.map((header, index) => (
          <Column
            key={index}
            header={<Cell>{header}</Cell>}
            renderCell={(rowIndex: number) => {
              const processedCell = processedRows[rowIndex]?.[index];
              if (!processedCell) {
                return <Cell />;
              }
              return (
                <CellWithCitations
                  showCitations={showCitations}
                  citationElements={processedCell.citationElements}
                  nonCitationElements={processedCell.nonCitationElements}
                />
              );
            }}
          />
        ))}
      </Table>
    </div>
  );
}

function ExpandedTableModal({
  opened,
  onClose,
  title,
  subtitle,
  minInlineTableWidthPx,
  headers,
  rowsForCopy,
  tableContentProps,
}: {
  opened: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  minInlineTableWidthPx?: number;
  headers: Array<string>;
  rowsForCopy: Array<Array<Array<React.ReactNode>>>;
  tableContentProps: Omit<
    LLMMarkdownTableContentProps,
    | 'onExpandClick'
    | 'showInlineHeaderActions'
    | 'showExpandedCopyButton'
    | 'footer'
    | 'headers'
    | 'rowsForCopy'
  >;
}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, theme} = useTableStyles();
  const displayTitle = title?.trim() ? title.trim() : DEFAULT_EXPANDED_TABLE_TITLE;
  const displaySubtitle = subtitle?.trim();
  const tableMinWidthStyle =
    minInlineTableWidthPx != null ? {minWidth: `min(${minInlineTableWidthPx}px, 95vw)`} : undefined;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <>
          <Text color={theme.colors.gray[9]} size={theme.fontSizes.lg} fw={600}>
            {displayTitle}
          </Text>
          {displaySubtitle != null && displaySubtitle.length > 0 && (
            <Text className={classes.expandedModalSubtitle}>{displaySubtitle}</Text>
          )}
        </>
      }
      withCloseButton={true}
      centered={true}
      // Mantine turns `size` into the panel's flex-basis, so `fit-content` sizes the modal to the table
      // instead of always filling the viewport. The 2.5vw gutters cap it at 95vw for very wide tables.
      size="fit-content"
      xOffset="2.5vw"
      closeOnClickOutside={true}
      closeOnEscape={true}
      withinPortal={!isNodeTesting}
      closeButtonProps={{
        'aria-label': 'Close expanded table',
      }}
      classNames={{
        content: classes.expandedModalContent,
        header: classes.expandedModalHeader,
        body: classes.expandedModalBody,
      }}
      styles={{
        content: tableMinWidthStyle,
      }}
    >
      <Box className={classes.expandedTablePanelScroll}>
        <Box className={classes.expandedTableOverlayGrid}>
          <Box className={classes.expandedTableCopyActions} data-llm-table-expanded-copy={true}>
            <CopyTableButton headers={headers} rows={rowsForCopy} />
          </Box>
          <Box className={classes.expandedTableScrollHost}>
            <LLMMarkdownTableContent
              {...tableContentProps}
              headers={headers}
              rowsForCopy={rowsForCopy}
              footer={undefined}
              fitTableToContent={false}
              onExpandClick={onClose}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

export default function TableWithCitations({
  children,
  classNames: markdownTableClassNames,
  title,
  subtitle,
}: TableWithCitationsProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useTableStyles();
  const [showCitations, setShowCitations] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [minInlineTableWidthPx, setMinInlineTableWidthPx] = useState<number | undefined>();
  const inlineTableContainerRef = useRef<HTMLDivElement>(null);

  const handleExpandClick = useCallback(() => {
    setIsExpanded((expanded) => {
      if (!expanded && inlineTableContainerRef.current != null) {
        setMinInlineTableWidthPx(measureInlineTableWidthPx(inlineTableContainerRef.current));
      }
      return !expanded;
    });
  }, []);

  const handleCloseExpanded = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const tableData = parseTableData(children);
  if (!tableData) {
    return <table>{children}</table>;
  }

  const {headers, rows} = tableData;

  const processedRows = rows.map((row) => row.map((cellContent) => splitContentByCitations(cellContent)));
  const hasCitations = processedRows.some((row) => row.some((cell) => cell.citationElements.length > 0));

  const footer = hasCitations ? (
    <Switch
      classNames={{
        body: css(styles.footerSwitchBody),
      }}
      checked={showCitations}
      onChange={(event) => {
        setShowCitations(event.currentTarget.checked);
      }}
      labelPosition="right"
      label={<Text fw="normal">{showCitations ? 'Hide citations' : 'Show all citations'}</Text>}
    />
  ) : undefined;

  const rowsForCopy: Array<Array<Array<React.ReactNode>>> = processedRows.map((row) =>
    row.map((cell) => cell.nonCitationElements)
  );

  const expandedTableContentProps = {
    processedRows,
    showCitations,
    tableClassName: classes.expandedSimpleTable,
    markdownTableClassNames,
  };

  return (
    <Box ref={inlineTableContainerRef}>
      <LLMMarkdownTableContent
        headers={headers}
        processedRows={processedRows}
        showCitations={showCitations}
        footer={footer}
        rowsForCopy={rowsForCopy}
        onExpandClick={handleExpandClick}
        showInlineHeaderActions={true}
        markdownTableClassNames={markdownTableClassNames}
      />
      <ExpandedTableModal
        opened={isExpanded}
        onClose={handleCloseExpanded}
        title={title}
        subtitle={subtitle}
        minInlineTableWidthPx={minInlineTableWidthPx}
        headers={headers}
        rowsForCopy={rowsForCopy}
        tableContentProps={expandedTableContentProps}
      />
    </Box>
  );
}

export const styles = StyleSheet.create({
  footerSwitchBody: {
    alignItems: 'center',
  },
  hiddenCitation: {
    visibility: 'hidden',
  },
});
